import React, { useState, useEffect } from 'react';
import { 
  FileText, Download, Search, Filter, Calendar, 
  User, Package, ShoppingCart, Eye, Printer
} from 'lucide-react';

interface Assignment {
  id: number;
  type: 'equipment' | 'consumable';
  equipment_id?: number;
  consumable_id?: number;
  equipment_name?: string;
  consumable_name?: string;
  user_id: number;
  user_name: string;
  assigned_by: string;
  assigned_date: string;
  return_date?: string;
  quantity?: number;
  unit_price?: number;
  total_price?: number;
  condition_on_assignment?: string;
  condition_on_return?: string;
  assignment_type?: string;
  status: string;
  notes: string;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  employee_id: string;
  department: string;
  position: string;
}

const AssignmentFormPage: React.FC = () => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchAssignments();
    fetchUsers();
  }, [currentPage, selectedUser, selectedType, selectedStatus, dateFrom, dateTo]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(selectedUser && { user_id: selectedUser }),
        ...(selectedType && { type: selectedType }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo })
      });

      // Ekipman ve sarf malzeme zimmetlerini birlikte al
      const [equipmentRes, consumableRes] = await Promise.all([
        fetch(`/api/equipment-assignments?${params}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        fetch(`/api/consumable-assignments?${params}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        })
      ]);

      let allAssignments: Assignment[] = [];

      if (equipmentRes.ok) {
        const equipmentData = await equipmentRes.json();
        const equipmentAssignments: Assignment[] = equipmentData.data.map((item: any) => ({
          ...item,
          type: 'equipment',
          equipment_name: `${item.equipment_brand} ${item.equipment_model}`,
          user_name: `${item.user_first_name} ${item.user_last_name}`
        }));
        allAssignments = [...allAssignments, ...equipmentAssignments];
      }

      if (consumableRes.ok) {
        const consumableData = await consumableRes.json();
        const consumableAssignments: Assignment[] = consumableData.data.map((item: any) => ({
          ...item,
          type: 'consumable',
          consumable_name: item.consumable_name,
          user_name: `${item.user_first_name} ${item.user_last_name}`
        }));
        allAssignments = [...allAssignments, ...consumableAssignments];
      }

      // Tarihe göre sırala
      allAssignments.sort((a, b) => new Date(b.assigned_date).getTime() - new Date(a.assigned_date).getTime());

      setAssignments(allAssignments);
    } catch (error) {
      console.error('Zimmet verileri yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
      }
    } catch (error) {
      console.error('Kullanıcılar yükleme hatası:', error);
    }
  };

  const generateAssignmentForm = async (assignment: Assignment) => {
    try {
      const endpoint = assignment.type === 'equipment' 
        ? `/api/equipment/${assignment.equipment_id}/assignment-form/${assignment.id}`
        : `/api/consumables/${assignment.consumable_id}/assignment-form/${assignment.id}`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `zimmet_formu_${assignment.id}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Form oluşturma hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          message: 'Zimmet formu oluşturulamadı'
        }
      }));
    }
  };

  const generateBulkReport = async () => {
    try {
      const params = new URLSearchParams({
        ...(selectedUser && { user_id: selectedUser }),
        ...(selectedType && { type: selectedType }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo })
      });

      const response = await fetch(`/api/assignments/bulk-report?${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `zimmet_raporu_${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Toplu rapor oluşturma hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          message: 'Rapor oluşturulamadı'
        }
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Zimmet Formları ve Raporları
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Zimmet işlemlerini görüntüleyin ve formları çıkarın
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={generateBulkReport}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Toplu Rapor
          </button>
        </div>
      </div>

      {/* Filtreler */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Kullanıcı
            </label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="">Tüm Kullanıcılar</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tür
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="">Tüm Türler</option>
              <option value="equipment">Ekipman</option>
              <option value="consumable">Sarf Malzeme</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Durum
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="">Tüm Durumlar</option>
              <option value="active">Aktif</option>
              <option value="returned">İade Edildi</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Başlangıç Tarihi
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Bitiş Tarihi
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={() => {
              setSelectedUser('');
              setSelectedType('');
              setSelectedStatus('');
              setDateFrom('');
              setDateTo('');
              setCurrentPage(1);
            }}
            className="flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtreleri Temizle
          </button>
        </div>
      </div>

      {/* Zimmet Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  Tür
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  Öğe
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  Kullanıcı
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  Zimmet Tarihi
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  Miktar/Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-600">
              {assignments.map((assignment) => (
                <tr key={`${assignment.type}-${assignment.id}`} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {assignment.type === 'equipment' ? (
                        <Package className="w-5 h-5 text-primary mr-2" />
                      ) : (
                        <ShoppingCart className="w-5 h-5 text-green-600 mr-2" />
                      )}
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {assignment.type === 'equipment' ? 'Ekipman' : 'Sarf Malzeme'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {assignment.equipment_name || assignment.consumable_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {assignment.user_name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(assignment.assigned_date).toLocaleDateString('tr-TR')}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {assignment.type === 'consumable' ? (
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {assignment.quantity} adet
                      </div>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        assignment.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}>
                        {assignment.status === 'active' ? 'Aktif' : 'İade Edildi'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => generateAssignmentForm(assignment)}
                        className="text-primary hover:text-primary-dark dark:text-primary dark:hover:text-accent"
                        title="Zimmet Formu Çıkar"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => generateAssignmentForm(assignment)}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                        title="Yazdır"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => {
                          const detailUrl = assignment.type === 'equipment' 
                            ? `equipment/${assignment.equipment_id}/assignments`
                            : `consumables/${assignment.consumable_id}/assignments`;
                          window.dispatchEvent(new CustomEvent('navigate', { 
                            detail: detailUrl 
                          }));
                        }}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                        title="Detayları Görüntüle"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {assignments.length === 0 && !loading && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Zimmet kaydı bulunamadı
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Seçilen kriterlere uygun zimmet işlemi bulunmuyor.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentFormPage;